import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, AlertTriangle } from 'lucide-react';
import { useHistory, useLocation } from 'react-router-dom';
import AlertNotification from '../../components/AlertNotification.js';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal.js';
import TradeInStepper from '../../components/TradeInStepper.js';
import noImage from '../../assets/img/brand/noImage.png';
import apiHelper from '../../apiHelper.js';
import {
    recalcDeductionResults, evaluateDeductionFormula, renumberFormulaRefs, deductionDisplayNumbers,
} from '../../utils/deductionFormula.js';

// ─── Constants ────────────────────────────────────────────────────────────────
const YELLOW      = '#FFEB00';
const BASE_TYPES  = ['Price', 'WhatsApp'];
const DED_TYPES   = ['Numeric', 'Formula', 'N/A', 'WhatsApp'];

// Build deduction rows from API-fetched definitions for a given device + condition.
// `savedDeductions` (ERP-5008) are this item's actual previously-saved deduction values
// (from the GET items API) — when present for a given definition, they override the
// template default so a re-save doesn't wipe out what was already entered.
function buildDeductionsForRow(listingDevice, condition, defs, savedDeductions) {
    const isPhone = (listingDevice || '').toLowerCase() === 'phone';
    const isNew   = (condition || '').toLowerCase() === 'new';
    const key     = isPhone ? (isNew ? 'phone_new' : 'phone_used') : (isNew ? 'not_phone_new' : 'not_phone_used');
    const savedById = {};
    (savedDeductions || []).forEach(sd => { savedById[sd.deduction_id] = sd; });
    // A saved item keeps exactly the deductions it has: its prices are Trade-In Pricing's to
    // change, so a deduction added later (ERP-5301 "Opened and Not Activated") isn't slipped in
    // with a default on an unrelated edit — the pricing upload gives it its value. Only a new
    // row starts from the defaults.
    const isSaved = Boolean(savedDeductions && savedDeductions.length);
    const kept = defs.filter(def => !isSaved || savedById[def.id]);
    // `no` is the ID the user sees and writes formulas in (ERP-5301: list order, so "Opened and
    // Not Activated" is 2 and "Within 3 Days" 3). Formulas arrive in definition ids and are
    // renumbered here; Save puts the ids back (toStoredFormulas).
    const numbers = deductionDisplayNumbers(kept.map(def => [def.id, def.sort_order]));
    return kept.map(def => {
        const cell  = def[key] || {};
        const saved = savedById[def.id];
        const no    = numbers ? numbers[def.id] : def.id;
        if (saved) {
            return {
                id:       def.id,
                no,
                name:     def.name,
                dataType: saved.deduction_value_type || cell.type || 'N/A',
                value:    saved.deduction_numeric_value  ?? '',
                formula:  renumberFormulaRefs(saved.deduction_formula_text || '', numbers),
                result:   saved.deduction_formula_result ?? '',
                locked:   !saved.is_editable,
            };
        }
        return {
            id:       def.id,
            no,
            name:     def.name,
            dataType: cell.type    || 'N/A',
            value:    cell.value   || '',
            formula:  renumberFormulaRefs(cell.formula || '', numbers),
            result:   cell.result  || '',
            locked:   cell.locked  ?? true,
        };
    });
}

// A row's deductions with their formulas back in definition ids — what the backend stores and
// the Excel export resolves. Rows without a `no` (state from before numbering) are already ids.
function toStoredFormulas(deductions) {
    const ids = {};
    deductions.forEach(d => { if (d.no != null) ids[d.no] = d.id; });
    return deductions.map(d => ({ ...d, formula: renumberFormulaRefs(d.formula, ids) }));
}

// A brand-new row added in Step 2 via "Add Listing Item" (never saved to the backend) carries
// a client-only id in this form — it has nothing to preserve as a draft if left inactive.
const isNewClientRow = (id) => typeof id === 'string' && id.startsWith('new-');

// ─── Build expanded rows (one per item × condition) ───────────────────────────
// Sort order (priority high → low):
//   1. Edit mode: newly-added (sku, condition) pairs first, existing (old) pairs after  [40-1]
//   2. Within each group: condition "New" before "Used"                                 [20-2]
//   3. Within same condition: preserve SKU order from Step 2                            [20-2]
//
// isNew = true  → (sku, condition) pair was NOT in the last save → editable
// isNew = false → already saved → read-only (price & deductions locked)
function buildExpandedRows(listingItems) {
    const all = [];
    (listingItems || []).forEach(item => {
        const sc = item.savedConditions || { new: false, used: false };
        // skip items with no condition or unchecked
        // A listing item can be active in New, Used, or both — one expanded row per active condition
        if (item.conditionNew) {
            all.push({
                id:             item.id,
                // New/Used are separate backend rows sharing one merged UI row (see Step 2) —
                // each expanded row must carry its OWN condition's real id, not the merged `item.id`,
                // or the backend can only preserve one condition's Open ID on a Storage/Color edit.
                itemBackendId:  item.newItemId ?? null,
                // Previously-saved Base Price + Deductions for this specific condition (ERP-5008) —
                // null for a genuinely new row, so its pricing still starts blank as before.
                savedPricing:   item.newPricing ?? null,
                image:          item.image,
                uploadedImages: item.uploadedImages || [],
                removedImageIds: item.removedImageIds || [],
                storage:        item.storage,
                color:          item.color,
                colorCode:      item.colorCode || '',
                rowKey:         `${item.id}-new`,
                condition:      'New',
                isNew:          !sc.new,
            });
        }
        if (item.conditionUsed) {
            all.push({
                id:             item.id,
                itemBackendId:  item.usedItemId ?? null,
                savedPricing:   item.usedPricing ?? null,
                image:          item.image,
                uploadedImages: item.uploadedImages || [],
                removedImageIds: item.removedImageIds || [],
                storage:        item.storage,
                color:          item.color,
                colorCode:      item.colorCode || '',
                rowKey:         `${item.id}-used`,
                condition:      'Used',
                isNew:          !sc.used,
            });
        }
    });

    // Sort: newly-added items first, then by condition (New before Used)
    // Stable sort preserves original SKU order within each group.
    all.sort((a, b) => {
        // Primary: newly-added before old (edit mode only; in add mode all isNew=true so no change)
        if (a.isNew !== b.isNew) return a.isNew ? -1 : 1;
        // Secondary: New condition before Used
        if (a.condition !== b.condition) return a.condition === 'New' ? -1 : 1;
        return 0; // preserve original SKU insertion order
    });

    return all;
}

// ─── Build initial pricing map { [rowKey]: { basePrice, deductions } } ────────
// A row with `savedPricing` (ERP-5008) is an already-saved condition whose Base Price
// and Deductions must survive an incidental save (e.g. editing another row, or Storage/
// Color on this same row) the same way its Open ID does — so it's seeded from the real
// saved values instead of blank. Only a genuinely new row/condition (savedPricing: null,
// i.e. "create" or "delist + recreate with diff storage/color") starts blank.
function buildInitialPricing(rows, listingDevice, defs) {
    const map = {};
    rows.forEach(r => {
        const saved = r.savedPricing;
        map[r.rowKey] = {
            basePrice: saved
                ? { type: saved.mode || 'Price', value: saved.mode === 'WhatsApp' ? '' : (saved.value ?? '') }
                : { type: 'Price', value: '' },
            deductions: buildDeductionsForRow(listingDevice, r.condition, defs, saved?.deductions),
        };
    });
    return map;
}


// ─── Small custom select ──────────────────────────────────────────────────────
function TinySelect({ value, options, onChange, error, fullWidth, disabled }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);
    return (
        <div ref={ref} style={{ position: 'relative', display: fullWidth ? 'block' : 'inline-block', width: fullWidth ? '100%' : undefined }}>
            <div
                onClick={() => { if (!disabled) setOpen(p => !p); }}
                style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    border: `1px solid ${error ? '#DC3545' : disabled ? '#6C757D' : '#212529'}`,
                    borderRadius: 8, padding: '10px', height: 40,
                    background: disabled ? '#F8F9FA' : 'white',
                    fontSize: 14, fontFamily: 'Montserrat', fontWeight: 400,
                    color: disabled ? '#6C757D' : '#212529',
                    whiteSpace: 'nowrap', userSelect: 'none', minWidth: fullWidth ? undefined : 90,
                    width: fullWidth ? '100%' : undefined, boxSizing: 'border-box',
                }}
            >
                <span style={{ flex: 1 }}>{value}</span>
                {!disabled && (
                    <ChevronDown size={12} color="#212529" style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                )}
            </div>
            {!disabled && open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
                    background: 'white', border: '1px solid #DEE2E6', borderRadius: 8,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: '100%', overflow: 'hidden',
                }}>
                    {options.map(opt => (
                        <div
                            key={opt}
                            onClick={() => { onChange(opt); setOpen(false); }}
                            style={{
                                padding: '10px', fontSize: 12, fontFamily: 'Montserrat', cursor: 'pointer',
                                background: opt === value ? '#FFFEF0' : 'white', color: '#212529',
                                fontWeight: 400,
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FFFEF0'}
                            onMouseLeave={e => e.currentTarget.style.background = opt === value ? '#FFFEF0' : 'white'}
                        >
                            {opt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── AED input ───────────────────────────────────────────────────────────────
function AedInput({ value, onChange, placeholder = '0.00', error, label }) {
    return (
        <div>
            {label && <span style={{ fontSize: 11, fontFamily: 'Montserrat', color: '#6C757D', display: 'block', marginBottom: 2 }}>{label}</span>}
            <div style={{
                display: 'flex', alignItems: 'center',
                border: `1px solid ${error ? '#DC3545' : '#212529'}`,
                borderRadius: 6, height: 36, overflow: 'hidden', background: 'white',
            }}>
                <span style={{ padding: '0 8px', fontSize: 11, fontFamily: 'Montserrat', color: '#6C757D', borderRight: '1px solid #DEE2E6', whiteSpace: 'nowrap', height: '100%', display: 'flex', alignItems: 'center', background: '#F8F9FA' }}>
                    AED
                </span>
                <input
                    type="number"
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    min="0"
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, fontFamily: 'Montserrat', padding: '0 8px', background: 'transparent', color: '#212529', minWidth: 0 }}
                />
            </div>
            {error && <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 2 }}>{error}</div>}
        </div>
    );
}

// The number a formula's {Base Price} reference resolves to. Only a "Price" row has one —
// a WhatsApp row has no price at all, so its formulas fail cleanly with "Base Price is not set".
const basePriceOperand = (entry) =>
    entry?.basePrice?.type === 'Price' ? (entry.basePrice.value ?? '') : '';

// Label above the Formula / Result fields inside a deduction row.
const fieldLabelStyle = { fontSize: 12, fontFamily: 'Montserrat', color: '#212529', fontWeight: 400 };

// ─── Calculated formula result ────────────────────────────────────────────────
// Read-only by design (ERP: Initial Pricing formula auto calculate) — the Result
// used to be a text box the user filled in themselves; now the system owns it.
//
// Three states, all decided by what recalcDeductionResults left on the row:
//   a number      — the formula evaluated on the last blur
//   "⚠ Error"     — it didn't (bad syntax, unknown reference, division by zero…),
//                   or the row is flagged: an empty formula the user has committed, or
//                   anything Confirm rejected. `flagged` isn't redundant with
//                   resultError — emptiness only counts as an error once the field has
//                   been left, so the modal owns that call, not the evaluator.
//   "—"           — nothing calculated yet: the formula is being typed, or is still
//                   blank and untouched. Results appear on commit, not per keystroke.
function FormulaResult({ row, flagged }) {
    if (row.result !== '' && row.result !== null && row.result !== undefined) {
        return <span style={{ color: '#212529' }}>{row.result}</span>;
    }
    if (row.resultError || flagged) {
        return (
            <>
                <AlertTriangle size={14} color="#FFC107" style={{ flexShrink: 0 }} />
                <span style={{ color: '#DC3545' }}>Error</span>
            </>
        );
    }
    return <span style={{ color: '#ADB5BD', fontWeight: 400 }}>&mdash;</span>;
}

// ─── Deductions Modal ─────────────────────────────────────────────────────────
function DeductionsModal({ deductions, basePrice, onCancel, onConfirm }) {
    // Saved formulas arrive with a stored Result, but the Result is derived now, so it's
    // recomputed on open — a formula that no longer evaluates (e.g. the row it references
    // was since blanked) must not keep showing a stale number. No errors are surfaced yet:
    // the designs only flag rows once the user tries to Confirm.
    const [rows, setRows]     = useState(() => recalcDeductionResults(deductions.map(d => ({ ...d })), basePrice).rows);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ show: false, type: '', message: '' });
    // Row ids whose formula the user has edited here. Only these may be reported as
    // invalid — see recalcDeductionResults. A formula loaded from the backend can use
    // Excel beyond what this evaluator parses (raw cell refs from a cross-row upload,
    // any function the spreadsheet contained), and must keep the result Excel gave it.
    const [ownedFormulas, setOwnedFormulas] = useState(() => new Set());

    // Generic field updater — also clears the matching error key
    const updateRow = (id, field, val) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));
        setErrors(prev => {
            const clone = { ...prev };
            if (clone[id]) {
                const { [field]: _, ...rest } = clone[id];
                if (Object.keys(rest).length) clone[id] = rest;
                else delete clone[id];
            }
            return clone;
        });
    };

    // Typing in a formula invalidates its Result immediately: the spec is to calculate only
    // once the user leaves the field, so the old number is dropped rather than left to look
    // like it matches the half-typed expression. `formulaInvalid` goes too — the red border
    // shouldn't follow the user while they're fixing the very thing it complains about.
    const updateFormula = (id, val) => {
        setOwnedFormulas(prev => prev.has(id) ? prev : new Set(prev).add(id));
        setRows(prev => prev.map(r => r.id === id ? { ...r, formula: val, result: '', resultError: '' } : r));
        setErrors(prev => {
            if (!prev[id]) return prev;
            const clone = { ...prev };
            const { formula: _f, formulaInvalid: _i, ...rest } = clone[id];
            if (Object.keys(rest).length) clone[id] = rest;
            else delete clone[id];
            return clone;
        });
    };

    // Blur is the calculation trigger ("show the result only when the user leaves the field").
    // Every Formula row is refreshed, not just the one blurred — a Numeric edit can change any
    // formula that references it.
    const recalcOnBlur = () => {
        setRows(prev => recalcDeductionResults(prev, basePrice, ownedFormulas).rows);
    };

    // Leaving a formula field commits it: calculate, and if it's empty say so. The check is
    // here rather than in recalcDeductionResults because "empty" only counts as an error once
    // the user has actually left the field — a blank row must not shout Error on open.
    const commitFormula = (id, formula) => {
        recalcOnBlur();
        if (!String(formula ?? '').trim()) {
            setErrors(prev => ({ ...prev, [id]: { ...(prev[id] || {}), formula: 'Value is required' } }));
        }
    };

    // The × button empties a required field, so it flags the row itself. It can't lean on the
    // input's blur: mousedown blurs before the click lands, so that blur still sees the
    // pre-clear text and an emptied field would sit unflagged until Confirm.
    const clearFormula = (id) => {
        updateFormula(id, '');
        setErrors(prev => ({ ...prev, [id]: { ...(prev[id] || {}), formula: 'Value is required' } }));
    };

    // Enter is the other way to say "I've stopped writing" — same commit as clicking away,
    // without needing to leave the field. preventDefault keeps it from submitting the page form.
    const commitOnEnter = (handler) => (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handler(); }
    };

    const validate = () => {
        const errs = {};
        rows.forEach(r => {
            if (r.dataType === 'Numeric') {
                if (!String(r.value).trim()) errs[r.id] = { ...(errs[r.id] || {}), value: 'Value is required' };
            } else if (r.dataType === 'Formula') {
                // One check covers both "field is empty" and "expression doesn't evaluate".
                // An empty formula gets the inline "Value is required" the designs show; a
                // malformed one only reddens the box, since its Result cell already reads Error.
                const outcome = evaluateDeductionFormula(r.formula, { row: r, rows, basePrice });
                if (outcome.empty) {
                    // Rule 4 — an empty formula field is always an error, saved or not.
                    errs[r.id] = { ...(errs[r.id] || {}), formula: 'Value is required' };
                } else if (!outcome.ok && ownedFormulas.has(r.id)) {
                    // Only a formula the user wrote here can be called wrong. An untouched one
                    // may be valid Excel this evaluator doesn't parse, and blocking the save on
                    // it would strand the user on data they never touched.
                    errs[r.id] = { ...(errs[r.id] || {}), formulaInvalid: true };
                }
            }
        });
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            setNotification({ show: true, type: 'error', message: 'Please fix the errors below' });
            return false;
        }
        return true;
    };

    // Confirm hands back freshly calculated Results — a user can Confirm straight after typing
    // without ever blurring the field, and that last edit still has to reach the payload.
    const handleConfirm = () => {
        if (!validate()) return;
        onConfirm(recalcDeductionResults(rows, basePrice, ownedFormulas).rows);
    };

    // ── Shared input style ───────────────────────────────────────────────────
    const valueInputStyle = (hasErr) => ({
        flex: 1, height: 40, border: `1px solid ${hasErr ? '#DC3545' : '#212529'}`,
        borderRadius: 8, fontSize: 14, fontFamily: 'Montserrat', padding: '10px',
        outline: 'none', background: 'white', color: '#212529', boxSizing: 'border-box', width: '100%',
    });

    const thS = {
        fontSize: 12, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529',
        padding: '10px ', background: '#E9ECEF', borderBottom: '1px solid #DEE2E6',
        whiteSpace: 'nowrap', textAlign: 'left',
    };
    const tdS = {
        padding: '10px', borderBottom: '1px solid #E9ECEF',
        verticalAlign: 'middle', fontSize: 12, fontFamily: 'Montserrat', color: '#212529', fontWeight: 400,
    };

    return (
        <>
            {/* Overlay */}
            <div className="drawer-overlay" onClick={onCancel} style={{ zIndex: 1040 }} />

            {/* Modal */}
            <div style={{
                position: 'fixed', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff', borderRadius: 8,
                boxShadow: '0px 4px 24px rgba(0,0,0,0.18)',
                zIndex: 1050, width: '92%', maxWidth: 900,
                maxHeight: '88vh', display: 'flex', flexDirection: 'column',
            }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 20px',  flexShrink: 0,background:'#F8F9FA',borderRadius:'12px 12px 0px 0px' }}>
                    <span style={{ fontWeight: 600, fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}>
                        View &amp; Edit Deductions
                    </span>
                    <button type="button" onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' }}>
                        <X size={18} color="#212529" />
                    </button>
                </div>

                {/* Toast — positioned just above the modal */}
                {notification.show && (
                    <div style={{
                        position: 'absolute', bottom: '90%', left: '50%',
                        transform: 'translateX(-50%)',
                        marginBottom: 8,
                        zIndex: 10, whiteSpace: 'nowrap',
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 16px', borderRadius: 12,
                        border: `1px solid ${notification.type === 'error' ? '#DC3545' : '#198754'}`,
                        background: notification.type === 'error' ? '#F8D7DA' : '#D1E7DD',
                        color: notification.type === 'error' ? '#58151C' : '#0A3622',
                        fontSize: 12, fontWeight: 700, fontFamily: 'Montserrat',
                        boxShadow: '0 4px 10px 0 rgba(0,0,0,0.12)',
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.99935 6.6665V9.99984M9.99935 13.3332H10.0077M18.3327 9.99984C18.3327 14.6022 14.6017 18.3332 9.99935 18.3332C5.39698 18.3332 1.66602 14.6022 1.66602 9.99984C1.66602 5.39746 5.39698 1.6665 9.99935 1.6665C14.6017 1.6665 18.3327 5.39746 18.3327 9.99984Z" stroke="#58151C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <strong>{notification.message}</strong>
                    </div>
                )}

                {/* Body */}
                <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ ...thS, width: 40 }}>ID</th>
                                <th style={{ ...thS, width: '28%' }}>Deduction Item</th>
                                <th style={{ ...thS, width: 160 }}>Data Type</th>
                                <th style={{ ...thS }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => {
                                const errs = errors[row.id] || {};
                                return (
                                    <tr key={row.id} style={{ background: idx % 2 === 0 ? 'white' : '#F8F9FA' }}>

                                        {/* ID — the number this row's formulas are written in here
                                            (its list position, see buildDeductionsForRow). Formula
                                            text is in the same numbers, so {N} means row N. */}
                                        <td style={{ ...tdS, background: 'inherit' }}>{row.no ?? row.id}</td>

                                        {/* Deduction Item */}
                                        <td style={{ ...tdS, background: 'inherit' }}>{row.name}</td>

                                        {/* Data Type — custom dropdown */}
                                        <td style={{ ...tdS, background: 'inherit' }}>
                                            <TinySelect
                                                value={row.dataType}
                                                options={DED_TYPES}
                                                onChange={val => {
                                                    // Recalculated in the same updater: switching a row's type blanks its
                                                    // value, which invalidates every formula pointing at this row.
                                                    setRows(prev => recalcDeductionResults(
                                                        prev.map(r => r.id === row.id
                                                            ? {
                                                                ...r, dataType: val,
                                                                value: '', formula: '', result: '', resultError: '',
                                                                // Mirrors the backend's own rule — is_editable =
                                                                // data_type in ('Numeric','Formula'). The payload sends
                                                                // is_editable: !locked, so leaving a stale locked here
                                                                // saved an "editable" WhatsApp/N/A row.
                                                                locked: val === 'N/A' || val === 'WhatsApp',
                                                            }
                                                            : r
                                                        ),
                                                        basePrice,
                                                        ownedFormulas
                                                    ).rows);
                                                    setErrors(prev => { const c = { ...prev }; delete c[row.id]; return c; });
                                                }}
                                                fullWidth
                                            />
                                        </td>

                                        {/* Value */}
                                        <td style={{ ...tdS, background: 'inherit' }}>

                                            {/* ── Numeric: single input with × clear ── */}
                                            {row.dataType === 'Numeric' && (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                        <input
                                                            type="text"
                                                            value={row.value}
                                                            onChange={e => updateRow(row.id, 'value', e.target.value)}
                                                            onBlur={recalcOnBlur}
                                                            onKeyDown={commitOnEnter(recalcOnBlur)}
                                                            style={valueInputStyle(Boolean(errs.value))}
                                                        />
                                                        {row.value !== '' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => updateRow(row.id, 'value', '')}
                                                                style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#ADB5BD' }}
                                                            >
                                                                <X size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {errs.value && <span style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat' }}>{errs.value}</span>}
                                                </div>
                                            )}

                                            {/* ── Formula: Formula input = calculated Result ── */}
                                            {row.dataType === 'Formula' && (
                                                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                                    {/* Formula field */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 140 }}>
                                                        <span style={fieldLabelStyle}>Formula:</span>
                                                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                                            <input
                                                                type="text"
                                                                value={row.formula}
                                                                onChange={e => updateFormula(row.id, e.target.value)}
                                                                onBlur={() => commitFormula(row.id, row.formula)}
                                                                onKeyDown={commitOnEnter(() => commitFormula(row.id, row.formula))}
                                                                // placeholder="{Base Price}-20"
                                                                style={{ ...valueInputStyle(Boolean(errs.formula || errs.formulaInvalid)), paddingRight: 28 }}
                                                            />
                                                            {row.formula !== '' && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => clearFormula(row.id)}
                                                                    style={{ position: 'absolute', right: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: '#ADB5BD' }}
                                                                >
                                                                    <X size={13} />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {errs.formula && <span style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat' }}>{errs.formula}</span>}
                                                    </div>

                                                    {/* "=" — the spacer label keeps it on the input's centre line, not the label's */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                                                        <span style={{ ...fieldLabelStyle, visibility: 'hidden' }}>=</span>
                                                        <span style={{ height: 40, display: 'flex', alignItems: 'center', fontSize: 14, fontFamily: 'Montserrat', color: '#212529' }}>=</span>
                                                    </div>

                                                    {/* Result — calculated, never typed */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 90, flexShrink: 0 }}>
                                                        <span style={fieldLabelStyle}>Result:</span>
                                                        <div style={{ height: 40, display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontFamily: 'Montserrat', fontWeight: 600 }}>
                                                            <FormulaResult row={row} flagged={Boolean(errs.formula || errs.formulaInvalid)} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* ── N/A: disabled readonly input ── */}
                                            {row.dataType === 'N/A' && (
                                                <input
                                                    type="text"
                                                    value=""
                                                    readOnly
                                                    placeholder="N/A"
                                                    style={{ ...valueInputStyle(false), border: '1px solid #6C757D', background: '#F8F9FA', color: '#6C757D', cursor: 'not-allowed', paddingRight: 10 }}
                                                />
                                            )}

                                            {/* ── WhatsApp: locked label, never editable ──
                                                There is no such thing as an editable WhatsApp value. The backend
                                                stores the row as deduction_locked_value_text = 'WhatsApp' and leaves
                                                deduction_numeric_value as None, so anything typed here was silently
                                                thrown away on save. It also derives is_editable from the type
                                                (`data_type in ('Numeric','Formula')`) and forces is_editable=False
                                                whenever an Excel upload turns a cell into N/A or WhatsApp — so a
                                                WhatsApp row carrying locked=false is stale state, not a real mode.
                                                Rendered like the N/A cell for consistency. */}
                                            {row.dataType === 'WhatsApp' && (
                                                <input
                                                    type="text"
                                                    value="WhatsApp"
                                                    readOnly
                                                    style={{ ...valueInputStyle(false), border: '1px solid #6C757D', background: '#F8F9FA', color: '#6C757D', cursor: 'not-allowed' }}
                                                />
                                            )}

                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '1px solid #E9ECEF', flexShrink: 0 }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            borderRadius: 8, border: '1px solid #212529', background: '#FFF',
                            boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                            display: 'flex', height: 34, minWidth: 100, padding: '10px',
                            justifyContent: 'center', alignItems: 'center', gap: 10,
                            color: '#212529', fontFamily: 'Montserrat', fontSize: 12,
                            fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal', cursor: 'pointer',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        style={{
                            display: 'flex', height: 34, minWidth: 100, padding: '10px',
                            justifyContent: 'center', alignItems: 'center', gap: 10,
                            borderRadius: 8, background: '#212529', border: 'none',
                            boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                            color: '#FFF', fontFamily: 'Montserrat', fontSize: 12,
                            fontStyle: 'normal', fontWeight: 600, lineHeight: 'normal', cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </>
    );
}

// ─── Condition Badge ──────────────────────────────────────────────────────────
function ConditionBadge({ condition }) {
    const isNew = condition === 'New';
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            padding: '5px 10px', borderRadius: 20,
            height: '20px',
minWidth: '72px',
            background: YELLOW ,
            color: '#212529',
            fontSize: 12, fontWeight: 600, fontFamily: 'Montserrat',
            whiteSpace: 'nowrap',
        }}>
            {condition}
        </span>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddEditTradeInListingStep3() {
    const history  = useHistory();
    const location = useLocation();

    const listing      = location.state?.listing      || {};
    const listingItems = location.state?.listingItems || [];
    const isEditMode   = Boolean(location.state?.isEdit);
    const breadcrumbs = ['Listing', 'Trade-In Listing', isEditMode ? 'Edit Listing' : 'Add Listing'];
    const restoredPricing = location.state?.pricingMap || null;
    // Carried straight through (ERP-5022). Step 3 doesn't show the picker, but the shared pool
    // and any not-yet-applied removals have to survive a trip here and back — dropping them on
    // Back would lose every file the user uploaded before reaching this step.
    const imagePool = location.state?.imagePool || null;
    const removedPoolImages = location.state?.removedPoolImages || null;

    // ── Build expanded rows once ──
    const [rows, setRows] = useState(() => buildExpandedRows(listingItems));
    // Seeded on the first render so every row has an entry before anything can be typed — the
    // Base Price inputs are live straight away, and editing a row with no entry crashed the page.
    // Base Price doesn't depend on the deduction definitions; the deductions do, so they stay
    // empty (and their controls disabled) until the fetch below fills them in.
    const [pricing, setPricing] = useState(() => ({
        ...buildInitialPricing(rows, listing.listingDevice, []),
        ...(restoredPricing || {}),
    }));
    const [deductionsReady, setDeductionsReady] = useState(() => (
        Boolean(restoredPricing) && rows.every(r => r.rowKey in restoredPricing)
    ));
    const [rowErrors, setRowErrors] = useState({}); // { [rowKey]: { value?, min?, max? } }

    // ── Drag-and-drop state ──
    const [dragIndex,     setDragIndex]     = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // ── Deductions modal ──
    const [deductionModal, setDeductionModal] = useState(null); // rowKey or null

    // ── Notification / Discard ──
    const [notification,    setNotification]    = useState({ show: false, type: '', message: '' });
    const [discardOpen,     setDiscardOpen]     = useState(false);
    const [isDirty,         setIsDirty]         = useState(location.state?.isDirty || false);
    const [isDiscarding,    setIsDiscarding]    = useState(false);
    const [nextLocation,    setNextLocation]    = useState(null);
    const [saving,          setSaving]          = useState(false);

    const isDirtyRef     = useRef(isDirty);
    const isDiscardingRef = useRef(false);
    useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

    // Navigation guard
    useEffect(() => {
        const unblock = history.block(loc => {
            if (isDirtyRef.current && !isDiscardingRef.current) {
                setNextLocation(loc);
                setDiscardOpen(true);
                return false;
            }
        });
        return () => unblock();
    }, [history]);

    // ── Before-unload (browser tab/window close) ──
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty && !isDiscarding) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty, isDiscarding]);

    // ── Fetch deduction definitions → build pricing (prefilling already-saved rows) ──
    useEffect(() => {
        let cancelled = false;
        async function init() {
            const restoredKeys = new Set(Object.keys(restoredPricing || {}));
            const missingRows = rows.filter(r => !restoredKeys.has(r.rowKey));

            // Every row already has an in-progress entry from navigating back — nothing to rebuild
            // (the seeded state above is already exactly that map).
            if (restoredPricing && missingRows.length === 0) return;

            // Fetch definitions + defaults from backend
            let defs = [];
            try {
                const res = await apiHelper('sales/trade-in-deduction-definitions/', 'GET');
                if (res?.ok) defs = await res.json();
            } catch (err) {
                console.error('Failed to fetch deduction definitions:', err);
            }

            // Build pricing from definitions, prefilling any already-saved row (ERP-5008) from
            // its own row.savedPricing (set in Step 2, matched by that condition's real backend
            // id — see ERP-4999). A previous version of this re-fetched saved items here and
            // matched them by Storage+Color+Condition instead: the moment the user edited
            // Storage/Color (even before saving), that match broke, pricing silently fell back
            // to blank, and the next Save persisted that blank over the real price.
            const fresh = buildInitialPricing(rows, listing.listingDevice, defs);

            if (cancelled) return;

            // Merge rather than replace: a row already present in the restored map (untouched
            // this session) keeps its in-progress base price/deductions as typed. Only rows
            // missing from it — because their condition was just toggled in Step 2, or they're
            // brand new — get the freshly-built entry, so an unaffected sibling row's price isn't
            // wiped out while the affected row's deduction rules still refresh correctly.
            //
            // A Base Price (or its Data Type) changed while the definitions were loading is kept,
            // and the new deductions' formulas are run against it — the built entry would
            // otherwise silently put back the value the user had just replaced.
            setPricing(prev => {
                const next = { ...prev };
                missingRows.forEach(r => {
                    const built = fresh[r.rowKey];
                    const typed = prev[r.rowKey]?.basePrice;
                    const edited = typed && (typed.type !== built.basePrice.type || typed.value !== built.basePrice.value);
                    next[r.rowKey] = edited ? recalcRowDeductions({ ...built, basePrice: typed }) : built;
                });
                return next;
            });
            setDeductionsReady(true);
        }
        init();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Pricing helpers ───────────────────────────────────────────────────────
    const formatPrice = (val) => {
        const num = parseFloat(String(val).replace(/,/g, ''));
        if (!val || isNaN(num)) return val;
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // A formula can reference {Base Price}, so editing the price restales every formula on
    // that row. Results are system-owned now, so they're recalculated right here instead of
    // waiting for the user to reopen the Deductions modal — otherwise a save could carry a
    // Result that no longer matches its own formula.
    const recalcRowDeductions = (entry) => {
        if (!entry) return entry;
        return {
            ...entry,
            deductions: recalcDeductionResults(entry.deductions || [], basePriceOperand(entry)).rows,
        };
    };

    const updateBasePrice = (rowKey, field, val) => {
        setIsDirty(true);
        setPricing(prev => ({
            ...prev,
            [rowKey]: recalcRowDeductions({
                ...prev[rowKey],
                basePrice: { ...prev[rowKey].basePrice, [field]: val },
            }),
        }));
        setRowErrors(prev => {
            const clone = { ...prev };
            if (clone[rowKey]) {
                const { [field]: _, ...rest } = clone[rowKey];
                if (Object.keys(rest).length) clone[rowKey] = rest;
                else delete clone[rowKey];
            }
            return clone;
        });
    };

    // ── Drag-and-drop handlers ────────────────────────────────────────────────
    const handleDragStart = (e, idx) => {
        setDragIndex(idx);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, idx) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (idx !== dragOverIndex) setDragOverIndex(idx);
    };

    const handleDrop = (e, idx) => {
        e.preventDefault();
        if (dragIndex === null || dragIndex === idx) {
            setDragIndex(null);
            setDragOverIndex(null);
            return;
        }
        setIsDirty(true);
        const reordered = [...rows];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(idx, 0, moved);
        setRows(reordered);
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const visibleRows = rows;

    // ── Validate ──────────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        visibleRows.forEach(r => {
            // Old rows in edit mode are locked — skip validation for them
            if (isEditMode && !r.isNew) return;
            const bp = pricing[r.rowKey]?.basePrice || {};
            const rowErr = {};
            if (bp.type === 'Price') {
                const raw = String(bp.value).trim();
                const numeric = Number(raw.replace(/,/g, ''));
                if (!raw) {
                    rowErr.value = 'Value is required';
                } else if (isNaN(numeric) || numeric < 0) {
                    rowErr.value = 'Base Price must be a non-negative number';
                }
            }
            if (Object.keys(rowErr).length) errs[r.rowKey] = rowErr;
        });
        setRowErrors(errs);
        return errs;
    };

    // ── Actions ───────────────────────────────────────────────────────────────
    // Converts an uploaded item image to base64 so it can travel in the same JSON save
    // call — mirrors AddSpu.js's convertSpuImage/toBase64 pattern for the spu images table,
    // since tradein_listing_item_images follows that same table pattern.
    const toBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    // Every file ever uploaded for a row (row.uploadedImages) is sent and persisted —
    // not just the one currently selected as the item's active image.
    // `skipNewUploads` is set for the second expanded row of a merged item (New + Used share
    // one `uploadedImages` list but are two separate backend rows) so a fresh upload isn't
    // persisted twice — once per condition — which otherwise doubles the upload history.
    const buildImageFields = async (rowOrItem, { skipNewUploads = false } = {}) => {
        const image = rowOrItem.image;
        // Already-persisted uploads (isSaved, loaded from a previous save) don't need
        // re-encoding/re-saving — only brand-new, not-yet-saved files do.
        const newUploads = skipNewUploads ? [] : (rowOrItem.uploadedImages || []).filter(u => !u.isSaved && u.file);
        const tradein_images = await Promise.all(newUploads.map(async (u) => ({
            base64:   await toBase64(u.file),
            filename: u.name || u.file.name,
            selected: Boolean(image?.isUploaded && image?.id === u.id),
        })));
        return {
            tradein_image_id: image?.isUploaded ? null : (image?.id || null),
            tradein_image_tab: image?.tab || 'spu',
            tradein_images,
            // Previously-saved uploads the user removed in Step 2. Deletions have to be named
            // explicitly: tradein_images above only ever carries brand-new files, so without
            // this the backend sees no difference between "removed one" and "changed nothing"
            // and the row survives every save.
            tradein_image_ids_removed: rowOrItem.removedImageIds || [],
            // The item is deliberately imageless — the SPU has no images either, so there is
            // nothing left to point at after a removal. Distinct from tradein_image_id: null,
            // which only means "unresolved" and lets the backend keep the previous snapshot.
            // WCM and the Sheet already substitute their own placeholder for an empty image.
            tradein_image_cleared: Boolean(image?.isPlaceholder),
        };
    };

    const handleSave = async () => {
        if (!deductionsReady || saving) return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setNotification({ show: true, type: 'error', message: 'Please fix the errors below' });
            return;
        }

        setSaving(true);
        try {

            // ── Build item rows (async — uploaded images are converted to base64) ──
            // Tracks which merged Step-2 item ids have already had their new uploads attached —
            // New/Used rows for the same item share one `uploadedImages` list, so only the first
            // expanded row should actually persist fresh uploads (see buildImageFields above).
            const uploadHandledForId = new Set();
            const activeItems = await Promise.all(rows.map(async (row, idx) => {
                const bp  = pricing[row.rowKey]?.basePrice  || { type: 'Price', value: '' };
                const ded = toStoredFormulas(pricing[row.rowKey]?.deductions || []);
                const skipNewUploads = uploadHandledForId.has(row.id);
                uploadHandledForId.add(row.id);
                return {
                    // Stable row identity — lets the backend match this to the same DB row across
                    // Storage/Color edits (preserving its uuid/Open ID) instead of re-deriving
                    // identity from storage+color+condition, which breaks on any edit to those fields.
                    // Each expanded row carries its OWN condition's real id (see buildExpandedRows) —
                    // never the merged Step-2 `row.id`, which New and Used rows would otherwise share.
                    tradein_listing_item_id: row.itemBackendId ?? null,
                    storage:          row.storage,
                    color:            row.color,
                    color_code:       row.colorCode || '',
                    tradein_condition: row.condition,
                    base_price_type:  bp.type,
                    base_price:       bp.type === 'Price' ? (bp.value ? String(bp.value).replace(/,/g, '') : null) : null,
                    ...(await buildImageFields(row, { skipNewUploads })),
                    sort_order:       idx,
                    deductions: ded.map(d => ({
                        deduction_id: d.id,
                        data_type:    d.dataType,
                        value:        d.value   || null,
                        formula:      d.formula || null,
                        result:       d.result  || null,
                        is_editable:  !d.locked,
                    })),
                };
            }));

            // Unchecked rows: save with no condition so data is preserved as draft
            // (skip brand-new, never-saved rows — there's nothing to preserve for those)
            const draftItems = await Promise.all(listingItems
                .filter(item => !(item.conditionNew || item.conditionUsed) && !isNewClientRow(item.id))
                .map(async (item, idx) => ({
                    // Filtered above to exclude brand-new client rows, so item.id is always a real backend id here
                    tradein_listing_item_id: item.id,
                    storage:           item.storage,
                    color:             item.color,
                    color_code:        item.colorCode || '',
                    tradein_condition: null,
                    base_price_type:   null,
                    base_price:        null,
                    ...(await buildImageFields(item)),
                    sort_order:        rows.length + idx,
                    deductions:        [],
                })));

            // ── Build payload ───────────────────────────────────────────────
            const payload = {
                listing_status:   listing.status || 'Active',
                spu_id:           listing.spu?.id,
                listing_device:   listing.listingDevice,
                listing_brand:    listing.listingBrand,
                listing_model:    listing.listingModel,
                // ERP-5298 Add After: the Listing this one follows (null = the top), placed by the
                // backend. Only sent when it changed, so an untouched edit keeps its place.
                ...(listing.addAfterChanged ? { add_after_id: listing.addAfter?.id ?? null } : {}),
                notes:            listing.notes || '',
                // The Listing Image is no longer picked (PRD 2.3): the backend gives the listing its
                // 1st item's image. Only removals from its saved upload history still travel.
                listing_image_ids_removed: listing.removedListingImageIds || [],
                ...(listing.id ? { listing_id: String(listing.id) } : {}),
                items: [...activeItems, ...draftItems],
            };

            // ── Choose endpoint ─────────────────────────────────────────────
            const isEdit   = isEditMode && listing.tradein_listing_id;
            const endpoint = isEdit
                ? `sales/trade-in-listings/${listing.tradein_listing_id}/update/`
                : 'sales/trade-in-listings/create/';
            const method   = isEdit ? 'PUT' : 'POST';

            const res     = await apiHelper(endpoint, method, payload);
            const resData = res ? await res.json().catch(() => null) : null;

            if (!res || !res.ok) {
                const msg = resData?.detail || resData?.message || 'Failed to save listing. Please try again.';
                setNotification({ show: true, type: 'error', message: msg });
                return;
            }

            console.log('[WCM sync]', isEdit ? 'update' : 'create', resData?.wcm_sync);

            // ── Success: redirect immediately, show toast on main page ────
            isDiscardingRef.current = true;
            setIsDiscarding(true);
            history.push('/trade-in-listing', { successMessage: 'Listing Saved Successfully' });

        } catch (err) {
            console.error('Failed to save listing:', err);
            setNotification({ show: true, type: 'error', message: 'An unexpected error occurred. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        isDiscardingRef.current = true;
        history.push(isEditMode ? '/trade-in-listing/edit-listing/items' : '/trade-in-listing/add-listing/items', {
            listing, listingItems, isEdit: isEditMode, isDirty,
            pricingMap: pricing,
            originalConditions: location.state?.originalConditions,
            // Forwarded untouched so the pool and pending removals survive Back (ERP-5022).
            imagePool,
            removedPoolImages,
        });
    };

    const handleDiscard = () => {
        if (isDirty) setDiscardOpen(true);
        else history.push('/trade-in-listing');
    };

    const handleDiscardConfirm = (confirm) => {
        if (confirm) {
            isDiscardingRef.current = true;
            setIsDiscarding(true);
            setIsDirty(false);
            setDiscardOpen(false);
            setTimeout(() => {
                if (nextLocation) {
                    const { pathname, state } = nextLocation.location || nextLocation;
                    const unblock = history.block(() => null);
                    history.push(pathname, state);
                    setTimeout(() => unblock(), 0);
                    setNextLocation(null);
                } else {
                    history.push('/trade-in-listing', { successMessage: 'Listing Discarded Successfully' });
                }
            }, 100);
        } else {
            setDiscardOpen(false);
            setNextLocation(null);
        }
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        fontSize: 12, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529',
        padding: '10px', background: '#E9ECEF', borderBottom: '1px solid #DEE2E6',
        whiteSpace: 'nowrap', textAlign: 'left',
    };

    const tdStyle = (rowIdx, hasErr) => ({
        padding: '10px', borderBottom: '1px solid #E9ECEF', verticalAlign: 'middle',
        background: rowIdx % 2 === 0 ? 'white' : '#F8F9FA', fontSize:"12px"
    });

    const actionBtnBase = {
        borderRadius: 8, height: 34, minWidth: 100, padding: '10px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 600, fontSize: 12, fontFamily: 'Montserrat',
        cursor: 'pointer', border: 'none', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
    };

    return (
        <div className="company-body main-company-body-inner new-look">

            {/* Toast */}
            {notification.show && (
                <AlertNotification
                    type={notification.type}
                    action="create"
                    recordName="Listing"
                    message={notification.message}
                    onClose={() => setNotification(prev => ({ ...prev, show: false }))}
                />
            )}

            {/* ── Breadcrumb ── */}
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, i) => (
                    <span
                        key={i}
                        className={`breadcrumb-item${crumb === 'Trade-In Listing' ? ' clickable' : ''}`}
                        style={{ cursor: crumb === 'Trade-In Listing' ? 'pointer' : 'default' }}
                        onClick={() => crumb === 'Trade-In Listing' && history.push('/trade-in-listing')}
                    >
                        {crumb}{i < breadcrumbs.length - 1 ? '  ' : ''}
                    </span>
                ))}
            </div>

            {/* Discard modal */}
            <DiscardConfirmationModal
                isOpen={discardOpen}
                onCancel={() => { setDiscardOpen(false); setNextLocation(null); }}
                onConfirm={handleDiscardConfirm}
                entityName="Trade-In Listing"
            />

            {/* Deductions modal */}
            {deductionModal && (
                <DeductionsModal
                    deductions={pricing[deductionModal]?.deductions || []}
                    // Formulas can reference {Base Price}; it lives on the row, not in the
                    // modal, and is fixed for as long as the modal is open.
                    basePrice={basePriceOperand(pricing[deductionModal])}
                    onCancel={() => setDeductionModal(null)}
                    onConfirm={(updatedDeductions) => {
                        setIsDirty(true);
                        setPricing(prev => ({
                            ...prev,
                            [deductionModal]: { ...prev[deductionModal], deductions: updatedDeductions },
                        }));
                        setDeductionModal(null);
                    }}
                />
            )}

            {/* Stepper */}
            <TradeInStepper currentStep={3} />

            {/* Page card */}
            <div style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

                {/* Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '20px', borderBottom: '1px solid #E9ECEF',
                    background: 'white', borderRadius: 8,
                }}>
                    <h5 style={{ margin: 0, fontSize: 20, fontWeight: 700, fontFamily: 'Montserrat', color: '#212529' }}>
                        Sort and Initialize Pricing
                    </h5>
                    <div style={{ display: 'flex', gap: 10 }}>
                        {/* Discard */}
                        <button type="button" onClick={handleDiscard} style={{ ...actionBtnBase, background: 'none', border: '1px solid #212529', color: '#212529' }}>
                            Discard
                        </button>
                        {/* Back */}
                        <button type="button" onClick={handleBack} style={{ ...actionBtnBase, background: 'none', border: '1px solid #212529', color: '#212529' }}>
                            Back
                        </button>
                        {/* Save */}
                        {/* Held until the deductions are built — saving earlier sent none at all */}
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving || !deductionsReady}
                            style={{ ...actionBtnBase, background: saving || !deductionsReady ? '#6C757D' : '#212529', color: 'white', cursor: saving ? 'not-allowed' : !deductionsReady ? 'progress' : 'pointer' }}
                        >
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </div>

                {/* Table card */}
                <div style={{ overflowX: 'auto',  background: 'white', marginTop: 13, borderRadius: 8 }}>
                    <p style={{ fontSize: 14, fontWeight: 600,
                         fontFamily: 'Montserrat',padding: '15px 20px', color: '#212529',
                         borderRadius: '12px 12px 0 0',
                            background: '#F8F9FA'
                         }}>
                        Sort and Set Initial Prices
                    </p>

                    {visibleRows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#ADB5BD', fontSize: 14, fontFamily: 'Montserrat' }}>
                            No items to price. Go back and select items.
                        </div>
                    ) : (
                        <div style={{padding:'20px'}}>

                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 780 }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle, width: '16%' }}>Listing Model</th>
                                    <th style={{ ...thStyle, width: '28%' }}>Item</th>
                                    <th style={{ ...thStyle, width: 110 }}>Condition</th>
                                    <th style={{ ...thStyle }}>Base Price</th>
                                    <th style={{ ...thStyle, width: 180 }}>Deductions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRows.map((row, rowIdx) => {
                                    const bp       = pricing[row.rowKey]?.basePrice || { type: 'Price', value: '' };
                                    const errs     = rowErrors[row.rowKey] || {};
                                    const hasErr   = Object.keys(errs).length > 0;
                                    const isOldRow = isEditMode && !row.isNew;
                                    const isDragging  = dragIndex === rowIdx;
                                    const isDragOver  = dragOverIndex === rowIdx && dragIndex !== rowIdx;

                                    return (
                                        <tr
                                            key={row.rowKey}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, rowIdx)}
                                            onDragOver={(e)  => handleDragOver(e, rowIdx)}
                                            onDrop={(e)      => handleDrop(e, rowIdx)}
                                            onDragEnd={handleDragEnd}
                                            style={{ opacity: isDragging ? 0.45 : 1, outline: isDragOver ? '2px solid #FFEB00' : undefined }}
                                        >
                                            {/* Listing Model */}
                                            <td style={tdStyle(rowIdx, hasErr)}>
                                                {listing.listingModel}
                                            </td>

                                            {/* Item */}
                                            <td style={tdStyle(rowIdx, hasErr)}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <div style={{ width: 32, height: 32,  overflow: 'hidden', flexShrink: 0, background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <img
                                                            src={row.image?.url || noImage}
                                                            alt={listing.listingModel}
                                                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                        />
                                                    </div>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529', marginBottom: 2 }}>
                                                            {row.storage}
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                            {(row.colorCode || row.color) && (
                                                                <div style={{
                                                                    width: 12, height: 12,
                                                                    borderRadius: '50%',
                                                                    border: '1px solid #212529',
                                                                    background: row.colorCode ? `#${row.colorCode.replace('#', '')}` : 'transparent',
                                                                    flexShrink: 0,
                                                                }} />
                                                            )}
                                                            {row.color && (
                                                                <span style={{ fontSize: 10, fontFamily: 'Montserrat', fontWeight: 400, color: '#6C757D' }}>
                                                                    {row.color}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Condition */}
                                            <td style={{...tdStyle(rowIdx, hasErr),fontWeight:600}}>
                                                <ConditionBadge condition={row.condition} />
                                            </td>

                                            {/* Base Price */}
                                            <td style={tdStyle(rowIdx, hasErr)}>
                                                {isOldRow ? (
                                                    <span style={{ fontSize: 12, fontFamily: 'Montserrat', color: '#ADB5BD', fontStyle: 'italic' }}>
                                                        Please Initiate Pricing to adjust
                                                    </span>
                                                ) : (
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>

                                                    {/* Left: Data Type label + dropdown */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 130 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529' }}>
                                                            Data Type:
                                                        </span>
                                                        <TinySelect
                                                            value={bp.type}
                                                            options={BASE_TYPES}
                                                            onChange={val => {
                                                                setIsDirty(true);
                                                                setPricing(prev => ({
                                                                    ...prev,
                                                                    [row.rowKey]: recalcRowDeductions({
                                                                        ...prev[row.rowKey],
                                                                        basePrice: { type: val, value: '' },
                                                                    }),
                                                                }));
                                                                setRowErrors(prev => { const c = { ...prev }; delete c[row.rowKey]; return c; });
                                                            }}
                                                        />
                                                    </div>

                                                    {/* Right: Value label + input */}
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1, minWidth: 150 }}>
                                                        <span style={{ fontSize: 12, fontWeight: 400, fontFamily: 'Montserrat', color: '#212529' }}>
                                                            Value:
                                                        </span>

                                                        {/* Price → SGD prefixed number input (non-negative) */}
                                                        {bp.type === 'Price' && (
                                                            <div>
                                                                <div style={{
                                                                    display: 'flex', alignItems: 'center',
                                                                    border: `1px solid ${errs.value ? '#DC3545' : '#6C757D'}`,
                                                                    borderRadius: 8, height: 40, overflow: 'hidden', background: 'white',
                                                                }}>
                                                                    <span style={{
                                                                        padding: '0 10px', fontSize: 12, fontFamily: 'Montserrat',
                                                                        color: '#6C757D', borderRight: '1px solid #6C757D',
                                                                        height: '100%', display: 'flex', alignItems: 'center',
                                                                        background: '#F8F9FA', whiteSpace: 'nowrap',
                                                                    }}>
                                                                        SGD
                                                                    </span>
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        value={bp.value}
                                                                        onChange={e => {
                                                                            const raw = e.target.value;
                                                                            // Allow digits, one decimal point, commas — block leading minus
                                                                            if (raw === '' || /^[0-9,]*\.?[0-9]*$/.test(raw)) {
                                                                                updateBasePrice(row.rowKey, 'value', raw);
                                                                            }
                                                                        }}
                                                                        onBlur={() => {
                                                                            const formatted = formatPrice(bp.value);
                                                                            if (formatted !== bp.value) updateBasePrice(row.rowKey, 'value', formatted);
                                                                        }}
                                                                        placeholder="0.00"
                                                                        style={{
                                                                            flex: 1, border: 'none', outline: 'none',
                                                                            fontSize: 14, fontFamily: 'Montserrat',
                                                                            padding: '0 10px', background: 'transparent',
                                                                            color: '#212529', minWidth: 0,
                                                                        }}
                                                                    />
                                                                </div>
                                                                {errs.value && (
                                                                    <div style={{ fontSize: 11, color: '#DC3545', fontFamily: 'Montserrat', marginTop: 3 }}>
                                                                        {errs.value}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* WhatsApp → locked display showing "WhatsApp" text */}
                                                        {bp.type === 'WhatsApp' && (
                                                            <div style={{
                                                                height: 40, border: '1px solid #6C757D', borderRadius: 8,
                                                                fontSize: 12, fontFamily: 'Montserrat', padding: '0 10px',
                                                                background: '#F8F9FA', color: '#ADB5BD',
                                                                display: 'flex', alignItems: 'center',
                                                                cursor: 'not-allowed', width: '100%', boxSizing: 'border-box',
                                                            }}>
                                                                WhatsApp
                                                            </div>
                                                        )}
                                                    </div>

                                                </div>
                                                )}
                                            </td>

                                            {/* Deductions */}
                                            <td style={tdStyle(rowIdx, hasErr)}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    {isOldRow ? (
                                                        <span style={{ fontSize: 12, fontFamily: 'Montserrat', color: '#ADB5BD', fontStyle: 'italic' }}>
                                                            Please Initiate Pricing to adjust
                                                        </span>
                                                    ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeductionModal(row.rowKey)}
                                                        disabled={!deductionsReady}
                                                        title={deductionsReady ? undefined : 'Loading deductions…'}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            fontSize: 12, fontFamily: 'Montserrat', fontWeight: 600,
                                                            color: deductionsReady ? '#212529' : '#ADB5BD',
                                                            background: 'none', border: `1px solid ${deductionsReady ? '#212529' : '#CED4DA'}`, borderRadius: 8,
                                                            padding: '10px', cursor: deductionsReady ? 'pointer' : 'progress', height: 34, whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        View and Edit
                                                    </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        title="Drag to reorder"
                                                        style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            background: 'none', border:'none',
                                                            width: 34, height: 34, cursor: 'grab', padding: 0, flexShrink: 0,
                                                        }}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                                            <path d="M3 9H15M3 13.5H15M3 4.5H15" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}